/**
 * Phone field split into a calling-code selector and a number field. The split is presentation
 * only: both edit the one PhoneValue the address store holds, so nothing here owns state.
 *
 * Lives beside the one form that uses it rather than among the shared inputs, and its styles are
 * scoped to the resolve-label-error modal: a second caller elsewhere means lifting the folder and
 * the scoping both.
 */
import { observer } from "mobx-react";
import { useCallback } from "react";
import { IValidationGroup } from "../../../../../../../../hooks/validation-group/validation-group-hook";
import { Input } from "../../../../../../../../components/inputs/input/input";
import { IOption, SelectDropdown } from "../../../../../../../../components/inputs/select-dropdown/select-dropdown";
import PhoneValue, { PHONE_ERROR, PhoneError } from "../../../../../stores/rma-action-store/phone-value";
import "./phone-input.scss";

export const INVALID_PHONE_MESSAGE = "Check the phone number for the selected country";
export const MISSING_CALLING_CODE_MESSAGE = "Pick the country code for this number";

/** One wording per error, so an error added to PhoneValue cannot reach the field without any. */
export const PHONE_ERROR_MESSAGE: Record<PhoneError, string> = {
    [PHONE_ERROR.MISSING_CALLING_CODE]: MISSING_CALLING_CODE_MESSAGE,
    [PHONE_ERROR.INVALID_NUMBER]: INVALID_PHONE_MESSAGE,
};

interface IProps {
    phone: PhoneValue;
    /** The countries to pick a code from, already labelled with the codes they dial. */
    options: IOption[];
    required?: boolean;
    validationGroup?: IValidationGroup;
}

const PhoneInput: React.FC<IProps> = observer(({ phone, options, required, validationGroup }) => {
    /**
     * A code that matches no option still has to show: a toll-free +800 belongs to no country, and
     * the country of a number may be missing from the list the owner passed.
     */
    const selectedCountryCodeOption = options.find((option) => String(option.value) === phone.country);
    const codeSelectorValue = selectedCountryCodeOption
        ?? (phone.callingCode ? { value: "", label: `+${phone.callingCode}` } : null);

    /**
     * Both callbacks are memoised because input-validation-hook keeps this one as a dependency of
     * its effect, and every run of that effect writes the field's state into the validation group's
     * observable map. A fresh identity each render would re-run it each render, and the write would
     * re-render whoever reads the group - which renders the field again.
     *
     * The selector stands for the calling code, so it is what goes red when there is none.
     */
    const countryValidation = useCallback(() => {
        if (phone.error === PHONE_ERROR.MISSING_CALLING_CODE) {
            return { isValid: false, messages: [PHONE_ERROR_MESSAGE[phone.error]] };
        }

        /** Nothing dialled at all: red because a phone is wanted, with no wrong number to explain. */
        if (!phone.callingCode) {
            return { isValid: !required, messages: [] };
        }

        return { isValid: true, messages: [] };
    }, [phone.error, phone.callingCode, required]);

    /**
     * Depends on the error itself, not on the parts behind it: picking another country leaves the
     * digits alone, and input-validation-hook re-runs only on those and on this callback's identity.
     */
    const numberValidation = useCallback(() => {
        /** Still invalid, but the selector above carries the reason; two messages would say it twice. */
        if (phone.error === PHONE_ERROR.MISSING_CALLING_CODE) {
            return { isValid: false, messages: [] };
        }

        if (phone.error) {
            return { isValid: false, messages: [PHONE_ERROR_MESSAGE[phone.error]] };
        }

        return { isValid: true, messages: [] };
    }, [phone.error]);

    return (
        <div className="phone-input">
            <div className="phone-input-code">
                <SelectDropdown
                    options={options}
                    value={codeSelectorValue}
                    onChange={(option) => phone.setCountry(option ? String(option.value) : undefined)}
                    validationGroup={validationGroup}
                    validationCallback={countryValidation}
                    showValidationMessage
                />
            </div>
            <div className="phone-input-number">
                <Input
                    type="tel"
                    value={phone.nationalNumber}
                    onChange={phone.setNumber}
                    required={required}
                    validationGroup={validationGroup}
                    validationCallback={numberValidation}
                    showValidationMessage
                />
            </div>
        </div>
    );
});

export default PhoneInput;
